import React, { useMemo } from 'react';
import { Tabs } from 'antd';
import moment from 'moment';
import ProductsTab from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/ProductsTab';
import AppliedAreaTab from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/AppliedAreaTab';
import DetailsTab, {
  DetailsTabExtraField
} from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab';
import useBoundingRectBySelector from 'screens/Applications&WorkOrders/Components/DetailsTabs/hooks/useBoundingRectBySelector';
import { OperationPropertyVO } from 'screens/Applications&WorkOrders/Components/hooks/useOperationProperties';
import {
  FullProduct,
  TankInformation
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { OperationDetails } from 'screens/Applications&WorkOrders/Components/types';
import { OperationType } from 'components/RecordsListView/constants';
import { parseWorkType } from 'screens/Applications&WorkOrders/Components/helpers';
/*
 * The tab strip's card chrome lives in this stylesheet, keyed off
 * `.operation-details-tabs`. Imported here rather than relied on: nothing else
 * on a Recommendation pulls `DetailsTabs` in, so without this the drawer's
 * appearance would depend on an unrelated screen already being in the bundle.
 */
import 'screens/Applications&WorkOrders/Components/DetailsTabs/index.css';
import {
  TAB_KEYS,
  TAB_LABELS,
  TAB_TYPE,
  OPERATION_DETAILS_TABS_WRAPPER_CLASS
} from 'screens/Applications&WorkOrders/Components/DetailsTabs/constants';
import {
  DATE_TIME_FORMAT,
  DRAWER_APPLIED_AREA_COLUMN_WIDTHS,
  DRAWER_CONTENT_BOTTOM_RESERVE_PX,
  DRAWER_DETAILS_CHROME_PX,
  DRAWER_DETAILS_FALLBACK_MAX_HEIGHT,
  DRAWER_HIDDEN_DETAIL_KEYS,
  DRAWER_TABS_WRAPPER_CLASS,
  DRAWER_TABS_WRAPPER_SELECTOR
} from './constants';

interface DrawerTabsProps {
  type: OperationType;
  details: OperationDetails;
  products: FullProduct[];
  propertiesData: OperationPropertyVO[];
  tankInformation?: TankInformation;
}

/**
 * The overview drawer's tab body: exactly Products, Applied Area and Details,
 * with Products active. Weather and Notes belong to the full detail panel and
 * are not part of the quick view.
 */
const DrawerTabs = ({
  type,
  details,
  products,
  propertiesData,
  tankInformation
}: DrawerTabsProps) => {
  // A viewport calc knows nothing about the drawer and capped the list well
  // short of the available space; measuring the wrapper uses what's there.
  const wrapperRect = useBoundingRectBySelector(DRAWER_TABS_WRAPPER_SELECTOR);

  const detailsMaxHeight = useMemo(() => {
    if (!wrapperRect?.height) return DRAWER_DETAILS_FALLBACK_MAX_HEIGHT;
    return `${Math.max(wrapperRect.height - DRAWER_DETAILS_CHROME_PX, 0)}px`;
  }, [wrapperRect]);

  const extraDetailFields = useMemo<DetailsTabExtraField[]>(() => {
    const formatDateTime = (value?: string) =>
      value ? moment(value).format(DATE_TIME_FORMAT) : '---';

    return [
      {
        key: 'startTime',
        label: 'Start Time',
        value: formatDateTime(details.startDate)
      },
      ...(type === OperationType.WORK_ORDER
        ? []
        : [
            {
              key: 'endTime',
              label: 'End Time',
              value: formatDateTime(details.endDate)
            }
          ]),
      {
        key: 'workType',
        label: 'Work Type',
        value: parseWorkType((details.type as unknown) as string) || '---'
      }
    ];
  }, [details, type]);

  const items = [
    {
      key: TAB_KEYS.PRODUCTS,
      label: TAB_LABELS.PRODUCTS,
      children: (
        <ProductsTab
          products={products}
          tankInformation={tankInformation}
          isLoading={false}
          disableSorting
          hideViewMore
          intrinsicScrollWidth
          wrapperSelector={DRAWER_TABS_WRAPPER_SELECTOR}
          containerBottomPaddingPx={DRAWER_CONTENT_BOTTOM_RESERVE_PX}
        />
      )
    },
    {
      key: TAB_KEYS.APPLIED_AREA,
      label: TAB_LABELS.APPLIED_AREA,
      children: (
        <AppliedAreaTab
          propertiesData={propertiesData}
          cropSeasonIds={details.cropSeasonIds || []}
          isLoading={false}
          hideViewOnMap
          disableSorting
          intrinsicScrollWidth
          wrapperSelector={DRAWER_TABS_WRAPPER_SELECTOR}
          containerBottomPaddingPx={DRAWER_CONTENT_BOTTOM_RESERVE_PX}
          columnWidths={DRAWER_APPLIED_AREA_COLUMN_WIDTHS}
        />
      )
    },
    {
      key: TAB_KEYS.DETAILS,
      label: TAB_LABELS.DETAILS,
      children: (
        <DetailsTab
          applicationDetails={details as any}
          type={type}
          extraFields={extraDetailFields}
          hiddenFieldKeys={DRAWER_HIDDEN_DETAIL_KEYS}
          singleColumn
          contentMaxHeight={detailsMaxHeight}
        />
      )
    }
  ];

  return (
    <div
      data-testid="linked-record-drawer-tabs"
      className={`${OPERATION_DETAILS_TABS_WRAPPER_CLASS} ${DRAWER_TABS_WRAPPER_CLASS} flex flex-col flex-1 min-h-0`}
    >
      <Tabs items={items} type={TAB_TYPE} className="leading-5" />
    </div>
  );
};

export default DrawerTabs;
